import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, User, Bot, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CallbackForm from "./CallbackForm";

const WELCOME = "Hi there, I'm Thandi — the virtual assistant for Dr Du Pré Louw's practice. How can I help you today? You can ask about services, hours or share your details and we'll call you back.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("chat"); // chat | share | callback
  const scrollRef = useRef(null);

  // Contact share mini-form state
  const [share, setShare] = useState({ name: "", phone: "", email: "", note: "" });
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const q = text.trim();
    if (!q || sending) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setText("");
    setSending(true);

    // Robust send: retry up to 3 times with exponential backoff.
    // Thandi is 24/7; only show the fallback after every retry has failed.
    const maxAttempts = 3;
    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const { data } = await api.post(
          "/chat",
          { session_id: sessionId, message: q },
          { timeout: 45000 } // 45s — LLM responses can be slow
        );
        setSessionId(data.session_id);
        setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
        setSending(false);
        return;
      } catch (err) {
        lastErr = err;
        console.warn(`Chat attempt ${attempt}/${maxAttempts} failed`, err?.response?.status, err?.message);
        if (attempt < maxAttempts) {
          // Exponential backoff: 1s, 2s
          await new Promise((r) => setTimeout(r, 1000 * attempt));
        }
      }
    }

    // All attempts failed — show a friendly fallback that highlights 24/7 alternatives
    console.error("Chat send failed after retries:", lastErr?.response?.status, lastErr?.response?.data, lastErr);
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        text:
          "I'm having a momentary connection hiccup — but I'm still on duty 24/7. Please try sending your message again, or tap 'Share details' so the practice can call you back. For urgent matters call 021 224 0204 or WhatsApp 072 399 4711.",
      },
    ]);
    setSending(false);
  };

  const shareDetails = async (e) => {
    e.preventDefault();
    if (!share.name || !share.phone) {
      toast.error("Please share your name and phone.");
      return;
    }
    setSharing(true);
    try {
      await api.post("/chat/contact", {
        session_id: sessionId,
        name: share.name,
        phone: share.phone,
        email: share.email || undefined,
        note: share.note || undefined,
      });
      setShared(true);
      toast.success("Thanks — the practice will call you back shortly.");
      // Echo the confirmation into the chat too
      setMessages((m) => [
        ...m,
        { role: "assistant", text: `Thank you, ${share.name.split(" ")[0]}! I've passed your details to Dr Louw's team — we'll call you on ${share.phone} shortly.` },
      ]);
    } catch {
      toast.error("Could not submit. Please call 021 224 0204.");
    } finally {
      setSharing(false);
    }
  };

  const resetShare = () => {
    setShared(false);
    setShare({ name: "", phone: "", email: "", note: "" });
    setTab("chat");
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-clay text-cream flex items-center justify-center shadow-xl chat-float-shadow animate-soft-pulse"
        aria-label="Open chat"
        data-testid="chat-widget-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-4 z-40 w-[min(380px,calc(100vw-2rem))] max-h-[70vh] bg-cream border border-border rounded-2xl overflow-hidden flex flex-col chat-float-shadow" data-testid="chat-widget-panel">
          <div className="bg-forest text-cream px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-clay flex items-center justify-center font-display text-lg">T</div>
            <div className="leading-tight">
              <div className="font-display text-lg">Thandi</div>
              <div className="text-[10px] tracking-widest uppercase text-sage">Virtual Assistant · Online</div>
            </div>
          </div>

          <div className="flex border-b border-border text-[11px]">
            <button
              onClick={() => setTab("chat")}
              className={`flex-1 py-2.5 uppercase tracking-widest font-semibold ${tab === "chat" ? "text-clay border-b-2 border-clay" : "text-ink/60"}`}
              data-testid="chat-tab-chat"
            >
              Chat
            </button>
            <button
              onClick={() => setTab("share")}
              className={`flex-1 py-2.5 uppercase tracking-widest font-semibold ${tab === "share" ? "text-clay border-b-2 border-clay" : "text-ink/60"}`}
              data-testid="chat-tab-share"
            >
              Share details
            </button>
            <button
              onClick={() => setTab("callback")}
              className={`flex-1 py-2.5 uppercase tracking-widest font-semibold ${tab === "callback" ? "text-clay border-b-2 border-clay" : "text-ink/60"}`}
              data-testid="chat-tab-callback"
            >
              Call me back
            </button>
          </div>

          {tab === "chat" ? (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-slim bg-cream" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    {m.role === "assistant" && (
                      <div className="w-7 h-7 rounded-full bg-sage flex items-center justify-center shrink-0">
                        <Bot className="w-3.5 h-3.5 text-forest" />
                      </div>
                    )}
                    <div className={`max-w-[78%] text-sm leading-relaxed px-3.5 py-2 rounded-2xl ${m.role === "user" ? "bg-clay text-cream rounded-br-sm" : "bg-cream-alt text-ink rounded-bl-sm"}`}>
                      {m.text}
                    </div>
                    {m.role === "user" && (
                      <div className="w-7 h-7 rounded-full bg-clay/20 flex items-center justify-center shrink-0">
                        <User className="w-3.5 h-3.5 text-clay" />
                      </div>
                    )}
                  </div>
                ))}
                {sending && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full bg-sage flex items-center justify-center"><Bot className="w-3.5 h-3.5 text-forest" /></div>
                    <div className="bg-cream-alt text-ink px-3.5 py-2 rounded-2xl">
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 bg-ink/50 rounded-full animate-pulse" />
                        <span className="w-1.5 h-1.5 bg-ink/50 rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                        <span className="w-1.5 h-1.5 bg-ink/50 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
                      </span>
                    </div>
                  </div>
                )}
                {messages.length >= 3 && !shared && (
                  <button
                    onClick={() => setTab("share")}
                    className="w-full mt-1 bg-sage/50 hover:bg-sage text-forest text-xs uppercase tracking-widest font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 transition-colors"
                    data-testid="chat-share-details-prompt"
                  >
                    <Sparkles className="w-3.5 h-3.5"/> Share my details with the practice
                  </button>
                )}
              </div>
              <form onSubmit={send} className="p-3 border-t border-border bg-cream flex gap-2" data-testid="chat-form">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message..."
                  className="bg-cream-alt border-border"
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={sending || !text.trim()} size="icon" className="bg-clay hover:bg-clay-dark shrink-0" data-testid="chat-send">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          ) : tab === "share" ? (
            <div className="p-4 overflow-y-auto scroll-slim bg-cream flex-1" data-testid="chat-share-panel">
              {shared ? (
                <div className="bg-sage/40 border border-sage rounded-xl p-6 text-center" data-testid="chat-share-success">
                  <CheckCircle2 className="w-9 h-9 text-forest mx-auto"/>
                  <h3 className="font-display text-xl text-forest mt-2">Sent to the practice.</h3>
                  <p className="text-ink/70 text-xs mt-2">We'll call <span className="font-medium">{share.phone}</span> shortly.</p>
                  <Button variant="outline" size="sm" className="mt-4 border-forest text-forest hover:bg-forest hover:text-cream" onClick={resetShare} data-testid="chat-share-reset">
                    Back to chat
                  </Button>
                </div>
              ) : (
                <form onSubmit={shareDetails} className="space-y-3" data-testid="chat-share-form">
                  <p className="text-xs text-ink/70">
                    Leave your details and Dr Louw's team will call you back — your chat will be attached for context.
                  </p>
                  <Input value={share.name} onChange={(e) => setShare({ ...share, name: e.target.value })} placeholder="Full name" className="bg-cream-alt border-border" data-testid="chat-share-name" />
                  <Input value={share.phone} onChange={(e) => setShare({ ...share, phone: e.target.value })} placeholder="Phone number" className="bg-cream-alt border-border" data-testid="chat-share-phone" />
                  <Input value={share.email} onChange={(e) => setShare({ ...share, email: e.target.value })} placeholder="Email (optional)" className="bg-cream-alt border-border" data-testid="chat-share-email" />
                  <Input value={share.note} onChange={(e) => setShare({ ...share, note: e.target.value })} placeholder="Short note (optional)" className="bg-cream-alt border-border" data-testid="chat-share-note" />
                  <Button type="submit" disabled={sharing} className="w-full bg-clay hover:bg-clay-dark text-cream" data-testid="chat-share-submit">
                    {sharing ? "Sending…" : "Send to the practice"}
                  </Button>
                  <p className="text-[10px] text-ink/60 text-center">By submitting, you consent to Dr Louw's practice contacting you.</p>
                </form>
              )}
            </div>
          ) : (
            <div className="p-4 overflow-y-auto scroll-slim bg-cream flex-1">
              <p className="text-xs text-ink/70 mb-3">
                Missed us on the phone? Leave your details — we never miss a callback.
              </p>
              <CallbackForm />
            </div>
          )}
        </div>
      )}
    </>
  );
}
